import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDebounce } from '../../../hooks/useDebounce';
import { clientsService } from '../../../services/clients.service';
import { Icon } from '@lnr-v2/ui';
import type { Client } from '@lnr-v2/types';
import styles from './ClientSelector.module.css';

export interface ClientSelectorProps {
  selectedClientId?: string;
  onChange: (clientId: string, client: Client | null) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  required?: boolean;
  className?: string;
}

export const ClientSelector: React.FC<ClientSelectorProps> = ({
  selectedClientId,
  onChange,
  placeholder = 'Search and select a client...',
  disabled = false,
  error,
  required = false,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const debouncedSearch = useDebounce(searchQuery, 300);

  const handleSelectClient = useCallback(
    (client: Client) => {
      setSelectedClient(client);
      onChange(client.id, client);
      setIsOpen(false);
      setSearchQuery('');
    },
    [onChange]
  );

  // Load initial client if selectedClientId is provided
  useEffect(() => {
    if (selectedClientId && !selectedClient) {
      loadInitialClient(selectedClientId);
    }
  }, [selectedClientId, selectedClient]);

  // Fetch clients based on search query
  useEffect(() => {
    if (isOpen) {
      fetchClients(debouncedSearch);
    }
  }, [debouncedSearch, isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setHighlightedIndex((prev) =>
            prev < clients.length - 1 ? prev + 1 : prev
          );
          break;
        case 'ArrowUp':
          event.preventDefault();
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
          break;
        case 'Enter':
          event.preventDefault();
          if (clients[highlightedIndex]) {
            handleSelectClient(clients[highlightedIndex]);
          }
          break;
        case 'Escape':
          event.preventDefault();
          setIsOpen(false);
          setSearchQuery('');
          break;
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }

    return undefined;
  }, [isOpen, clients, highlightedIndex, handleSelectClient]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (listRef.current && highlightedIndex >= 0) {
      const highlightedElement = listRef.current.children[
        highlightedIndex
      ] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  const loadInitialClient = async (clientId: string) => {
    try {
      const client = await clientsService.getById(clientId);
      setSelectedClient(client);
    } catch (error) {
      console.error('Failed to load initial client:', error);
    }
  };

  const fetchClients = async (search: string) => {
    setLoading(true);
    try {
      const response = await clientsService.getAll({
        page: 1,
        limit: 20,
        search: search || undefined,
        isActive: true,
      });
      setClients(response.items || []);
      setHighlightedIndex(0);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setSelectedClient(null);
    onChange('', null);
    setSearchQuery('');
    inputRef.current?.focus();
  };

  const handleInputFocus = () => {
    if (!disabled) {
      setIsOpen(true);
      if (!searchQuery && !selectedClient) {
        fetchClients('');
      }
    }
  };

  const displayValue = selectedClient
    ? selectedClient.name
    : searchQuery
      ? searchQuery
      : '';

  return (
    <div
      ref={containerRef}
      className={`${styles.container} ${disabled ? styles.disabled : ''} ${error ? styles.error : ''} ${className || ''}`}
    >
      <div className={styles.inputWrapper}>
        {/* Client logo/icon */}
        {selectedClient && !searchQuery && (
          <div className={styles.clientAvatar}>
            {selectedClient.logo ? (
              <img
                src={selectedClient.logo}
                alt={selectedClient.name}
                className={styles.clientLogo}
              />
            ) : (
              <div className={styles.clientInitial}>
                {selectedClient.name.charAt(0).toUpperCase()}
              </div>
            )}
          </div>
        )}

        {/* Search/display input */}
        <input
          ref={inputRef}
          type="text"
          className={styles.input}
          value={displayValue}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            if (selectedClient) {
              setSelectedClient(null);
              onChange('', null);
            }
          }}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          disabled={disabled}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-autocomplete="list"
          role="combobox"
          required={required}
        />

        {/* Actions */}
        <div className={styles.actions}>
          {selectedClient && !disabled && (
            <button
              type="button"
              className={styles.clearButton}
              onClick={handleClear}
              aria-label="Clear selection"
            >
              <Icon name="x" size="sm" />
            </button>
          )}
          <button
            type="button"
            className={styles.toggleButton}
            onClick={() => !disabled && setIsOpen(!isOpen)}
            disabled={disabled}
            aria-label={isOpen ? 'Close dropdown' : 'Open dropdown'}
          >
            <Icon name={isOpen ? 'chevron-up' : 'chevron-down'} size="sm" />
          </button>
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && !disabled && (
        <div className={styles.dropdown} role="listbox">
          {loading ? (
            <div className={styles.loading}>
              <div className={styles.spinner} />
              <span>Loading clients...</span>
            </div>
          ) : clients.length > 0 ? (
            <ul ref={listRef} className={styles.list}>
              {clients.map((client, index) => (
                <li
                  key={client.id}
                  className={`${styles.listItem} ${
                    index === highlightedIndex ? styles.highlighted : ''
                  } ${selectedClient?.id === client.id ? styles.selected : ''}`}
                  onClick={() => handleSelectClient(client)}
                  role="option"
                  aria-selected={selectedClient?.id === client.id}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  <div className={styles.clientInfo}>
                    <div className={styles.clientAvatar}>
                      {client.logo ? (
                        <img
                          src={client.logo}
                          alt={client.name}
                          className={styles.clientLogo}
                        />
                      ) : (
                        <div className={styles.clientInitial}>
                          {client.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div className={styles.clientDetails}>
                      <div className={styles.clientName}>{client.name}</div>
                      {client.industry && (
                        <div className={styles.clientIndustry}>
                          {client.industry}
                        </div>
                      )}
                    </div>
                  </div>
                  {selectedClient?.id === client.id && (
                    <Icon name="check" size="sm" className={styles.checkIcon} />
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <div className={styles.empty}>
              {searchQuery
                ? `No clients found for "${searchQuery}"`
                : 'No clients available'}
            </div>
          )}
        </div>
      )}

      {/* Error message */}
      {error && <div className={styles.errorMessage}>{error}</div>}
    </div>
  );
};
